import React, { useEffect, useState, useRef } from "react";
import { Download } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getAppointmentCounter, getAppointmentDetailData } from "../../redux/action/Appointment";
import { getOrganisationData } from "../../redux/api/Organisation";
import { getStatusColor, getStatusText } from "../../utils";
import { getPathSegments } from "../../utils/navigation";

// Helper function to format symptom levels
const formatSymptomLevel = (level) => {
  if (!level || level === "None") return "None";
  return level.charAt(0).toUpperCase() + level.toLowerCase().slice(1);
};

// Helper function to format date
const formatApiDate = (dateStr) => {
  if (!dateStr) return "";
  // Convert from yyyy-mm-dd to dd-mm-yyyy format
  const dateParts = dateStr.split('-');
  if (dateParts.length === 3) {
    return `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
  }
  return dateStr;
};

// Helper function to calculate age from date of birth
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return "";
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return `${age}yr / ${formatApiDate(dateOfBirth)}`;
};

// Header component with logo and branding
const PDFHeader = () => {
  return (
    <div className="relative w-full h-[45px] mb-20 pdf-header-running">
      {/* Blue header bar - exact from Figma */}
      <div className="absolute bg-[#4B61FF] h-[20.748px] left-0 top-0 w-full" />
      
      {/* Logo/Branding - exact positioning from Figma */}
      <div className="absolute h-[44.46px] left-0 top-[0.74px] w-[419.403px]">
        <img 
          alt="PhyberCare Logo" 
          
          src={require('../../assets/images/pdf_header.svg').default} 
        />
      </div>
    </div>
  );
};


// Footer component
const PDFFooter = () => {
  return (
    <div className="relative w-full mt-12 pb-8 pdf-footer-running">
      {/* Footer Content Section */}
      <div className="relative z-10">
        <div className="flex justify-between items-center">
          {/* Left Side - Logo and "Prepared using phyber" */}
          <div className="flex flex-col items-start justify-start">
            {/* Logo */}
            <div className="h-[29.827px] w-[105px] mb-2">
              <img 
                alt="Phyber Report Logo" 
                src={require('../../assets/images/pyhber_report_logo.png')}
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* "Prepared using phyber" text */}
            <div className="font-poppins font-normal leading-normal text-[#334155] text-[14px] tracking-[0.28px]">
              <p>Prepared using phyber</p>
            </div>
          </div>

          {/* Right Side - Disclaimer Text */}
          <div className="flex-1 text-center">
            <p className="font-poppins font-normal text-[#334155] text-[16px] leading-relaxed">
              This report contains sensitive health information and is intended only for authorized personnel.
            </p>
          </div>
        </div>
      </div>

      {/* Blue footer bar - positioned at the very bottom */}
      <div className="absolute bg-[#4B61FF] h-[20.748px] left-0 bottom-0 w-full" />
      
      {/* Logo/Branding - positioned on the blue bar */}
      <div className="absolute h-[44.46px] right-0 bottom-[0.74px] w-[419.403px]">
          <img 
            alt="PhyberCare Logo" 
            
            src={require('../../assets/images/pdf_footer.svg').default} 
          />
      </div>
    </div>
  );
};

// Risk status badge component
const RiskStatusBadge = ({ risk }) => {
  const getRiskStyles = (riskLevel) => {
    switch(riskLevel) {
      case "HIGH":
        return "bg-[#DC2626] text-white";
      case "MODERATE":
        return "bg-[#ff7439] text-white";
      case "LOW":
        return "bg-[#ffee33] text-[#171717]";
      case "NO_RISK":
        return "bg-[#B7E049] text-[#171717]";
      default:
        return "bg-[#ffee33] text-[#171717]";
    }
  };

  return (
    <div className={`px-3 py-0.5 rounded-[16px] flex items-center justify-center ${getRiskStyles(risk)}`}>
      <span className="font-poppins font-medium text-[14px] uppercase tracking-[0.5px]">
        {risk}
      </span>
    </div>
  );
};

// Status indicator with dot - matches SessionHistory configuration
const StatusIndicator = ({ session }) => {
  // Apply the same logic as SessionHistory component
  const getStatusDisplay = (session) => {
    // Check if appointment is in the future
    const appointmentDateTime = new Date(`${session.date}T${session.startTime}`);
    const now = new Date();
    const isUpcoming = appointmentDateTime > now;
    
    // Determine status display based on whether it's upcoming or past
    let statusColor, statusText;
    if (session.status === "SCHEDULED") {
      if (isUpcoming) {
        statusColor = getStatusColor("SCHEDULED");
        statusText = "Upcoming";
      } else {
        statusColor = getStatusColor("MISSED");
        statusText = "Missed";
      }
    } else if (session.status === "IN_COMPLETE") {
      // Check if recommendations are provided for incomplete appointments
      const hasRecommendationText = session.recommendationText && session.recommendationText.trim() !== "";
      const hasRecommendationFiles = session.prescriptions && session.prescriptions.some(p => p.prescriptionType === "RECOMMENDATION");
      const hasRecommendations = hasRecommendationText || hasRecommendationFiles;
      
      if (hasRecommendations) {
        // If recommendations are provided, show as COMPLETED
        statusColor = getStatusColor("COMPLETED");
        statusText = getStatusText("COMPLETED");
      } else {
        // No recommendations, show as IN_COMPLETE
        statusColor = getStatusColor("IN_COMPLETE");
        statusText = getStatusText("IN_COMPLETE");
      }
    } else {
      statusColor = getStatusColor(session.status);
      statusText = getStatusText(session.status);
    }
    
    return { statusColor, statusText };
  };

  const { statusColor, statusText } = getStatusDisplay(session);

  return (
    <div className="flex gap-2 items-center">
      <div className={`w-3 h-3 ${statusColor} rounded-full`} />
      <span className="font-poppins font-semibold text-[#4B61FF] text-[16px] font-semibold tracking-[0.32px]">
        {statusText}
      </span>
    </div>
  );
};

// Logo component for the PDF
const LogoSection = ({ organisationLogo }) => {
  const fallbackLogo = require('../../assets/images/ChatGPT Image Aug 12, 2025, 07_18_49 PM 1.png');
  const fallbackTextLogo = require('../../assets/images/ChatGPT Image Aug 12, 2025, 07_18_49 PM 2.png');
  
  return (
    <div className="relative h-20 mb-20">
      {organisationLogo ? (
        // Use organisation logo from API if available
        <div className="absolute h-20 left-0 top-0 w-[200px]">
          <img 
            alt="Organisation Logo" 
            src={organisationLogo}
            className="w-full h-full object-contain"
            onError={(e) => {
              // Fallback to default logo if organisation logo fails to load
              e.target.src = fallbackLogo;
            }}
          />
        </div>
      ) : (
        // Fallback to default logos
        <>
          {/* Logo 1 - Main circular logo */}
          <div className="absolute h-20 left-0 top-0 w-[71.5px]">
            <img 
              alt="PhyberCare Logo 1" 
              src={fallbackLogo}
              className="w-full h-full object-contain"
            />
          </div>
          
          {/* Logo 2 - Text logo */}
          <div className="absolute h-[51.5px] left-[75px] top-[14.25px] w-[125.667px]">
            <img 
              alt="PhyberCare Logo 2" 
              src={fallbackTextLogo}
              className="w-full h-full object-contain"
            />
          </div>
        </>
      )}
    </div>
  );
};

// PDF Download button component using native browser PDF
const PDFDownloadButton = ({ memberData }) => {
  const memberName = `${memberData?.firstName || ''} ${memberData?.lastName || ''}`.trim() || 'Member';

  const generatePDF = () => {
    // Set document title for PDF
    const originalTitle = document.title;
    document.title = `Member Summary Report - ${memberName}`;
    
    // Trigger browser's native print dialog which allows saving as PDF
    window.print();
    
    // Restore original title
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  return (
    <div className="fixed top-4 right-4 z-50 print:hidden">
      <button 
        onClick={generatePDF}
        className="btn-gradient text-white px-4 py-2 rounded-xl flex items-center gap-2 shadow-lg"
        title="Download PDF Report"
      >
        <Download className="h-4 w-4" />
        Download PDF Report
      </button>
    </div>
  );
};

// Main PDF Page Component
export default function PDFExportPage() {
  const dispatch = useDispatch();
  const params = useParams();
  const [memberData, setMemberData] = useState(null);
  const [sessionsData, setSessionsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Get Redux state
  const { data: appointmentData, loading: appointmentLoading } = useSelector((state) => state.APPOINTMENT_COUNTER || {});
  const { detail, detailLoading } = useSelector((state) => state.GET_APPOINTMENT_DETAIL || {});
  const { data: organisationData } = useSelector((state) => state.getOrgData || {});

  useEffect(() => {
    // Get appointment ID and user ID from URL parameters
    const { aid: appointmentId, uid: userId } = params;
    
    console.log("PDFExport - URL params:", { appointmentId, userId });
    
    if (appointmentId && userId) {
      console.log("PDFExport - Dispatching API calls");
      // Fetch appointment data and organisation data
      dispatch(getAppointmentCounter({}));
      dispatch(getAppointmentDetailData(appointmentId));
      dispatch(getOrganisationData());
    } else {
      console.log("PDFExport - Missing URL parameters, using fallback");
      // If no URL params, try to get from current page path for backward compatibility
      const pathParts = getPathSegments();
      if (pathParts.length >= 4) {
        const fallbackAid = pathParts[2];
        const fallbackUid = pathParts[3];
        if (fallbackAid && fallbackUid) {
          dispatch(getAppointmentCounter({}));
          dispatch(getAppointmentDetailData(fallbackAid));
          dispatch(getOrganisationData());
        }
      } else {
        // No valid parameters found, still fetch organisation data and set fallback
        dispatch(getOrganisationData());
        setIsLoading(false);
        setMemberData({
          firstName: "Sample",
          lastName: "Member",
          email: "sample@example.com",
          mobile: "1234567890",
          countryCode: "+1",
          gender: "male",
          location: "Sample Location"
        });
      }
    }
  }, [dispatch, params]);

  useEffect(() => {
    // Debug logging
    console.log("PDFExport - appointmentData:", appointmentData);
    console.log("PDFExport - detail:", detail);
    console.log("PDFExport - params:", params);
    console.log("PDFExport - detailLoading:", detailLoading);
    console.log("PDFExport - appointmentLoading:", appointmentLoading);
    
    // Process appointment data when available
    if (detail) {
      console.log("PDFExport - Detail received:", detail);
      
      // Handle different possible response structures for detail
      let memberDataFromAPI = null;
      if (detail.payload) {
        memberDataFromAPI = detail.payload;
      } else if (detail.data) {
        memberDataFromAPI = detail.data;
      } else if (typeof detail === 'object' && detail.firstName) {
        memberDataFromAPI = detail;
      }
      
      console.log("PDFExport - Extracted memberData:", memberDataFromAPI);
      
      if (memberDataFromAPI) {
        // Set member data from detail API
        setMemberData(memberDataFromAPI);
      
      if (appointmentData) {
        console.log("PDFExport - Raw appointmentData structure:", appointmentData);
        let allAppointments = [];
        
        // Try different ways to extract appointments from the data structure
        if (Array.isArray(appointmentData)) {
          allAppointments = appointmentData;
        } else if (typeof appointmentData === 'object' && appointmentData !== null) {
          // Try to get appointments from nested structure
          try {
            allAppointments = Object.values(appointmentData).flat().filter(Boolean);
          } catch (error) {
            console.log("PDFExport - Error extracting from Object.values:", error);
            allAppointments = [];
          }
          
          // If still empty, try other common patterns
          if (allAppointments.length === 0) {
            if (appointmentData.appointments && Array.isArray(appointmentData.appointments)) {
              allAppointments = appointmentData.appointments;
            } else if (appointmentData.data && Array.isArray(appointmentData.data)) {
              allAppointments = appointmentData.data;
            } else if (appointmentData.payload && Array.isArray(appointmentData.payload)) {
              allAppointments = appointmentData.payload;
            }
          }
        }
        
        console.log("PDFExport - Extracted allAppointments:", allAppointments);
        
        // Filter sessions for this member using the uid from params or detail payload
        const { uid: userId } = params;
        
        console.log("PDFExport - Filtering parameters:");
        console.log("  - userId from params:", userId, typeof userId);
        console.log("  - memberDataFromAPI:", memberDataFromAPI);
        console.log("  - pcUserId from memberData:", memberDataFromAPI?.pcUserId, typeof memberDataFromAPI?.pcUserId);
        console.log("  - All appointments count:", allAppointments.length);
        console.log("  - Sample appointment UIDs:", allAppointments.slice(0, 3).map(a => ({ appointmentId: a.appointmentId, uid: a.uid, type: typeof a.uid })));
        
        const memberSessions = allAppointments.filter(
          (appointment) => {
            const match1 = appointment.uid === parseInt(userId);
            const match2 = memberDataFromAPI && appointment.uid === memberDataFromAPI.pcUserId;
            const match3 = appointment.uid === userId; // In case it's stored as string
            const match4 = memberDataFromAPI && appointment.uid === parseInt(memberDataFromAPI.pcUserId);
            return match1 || match2 || match3 || match4;
          }
        );

        console.log("PDFExport - memberSessions found:", memberSessions.length);
        console.log("PDFExport - memberSessions:", memberSessions);

        // If no member sessions found, show all appointments for debugging
        let sessionsToProcess = memberSessions;
        if (memberSessions.length === 0 && allAppointments.length > 0) {
          console.log("PDFExport - No member sessions found, showing all appointments for debugging");
          sessionsToProcess = allAppointments.slice(0, 10); // Limit to first 10 for performance
        }

        // Process sessions data similar to SessionHistory component
        const processedSessions = sessionsToProcess
          .map((session, index) => {
            // Process prescriptions by type
            const prescriptionsByType = (session.prescriptions || []).reduce((acc, prescription) => {
              acc[prescription.prescriptionType] = prescription;
              return acc;
            }, {});

            return {
              ...session,
              sessionFindings: {
                selfHarmSymptoms: formatSymptomLevel(session.selfHarmSymptoms),
                depressionSymptoms: formatSymptomLevel(session.depressionSymptoms),
                anxietySymptoms: formatSymptomLevel(session.anxietySymptoms),
                angerIssues: formatSymptomLevel(session.angerIssues)
              },
              recommendations: {
                assessments: prescriptionsByType.ASSESSMENT ? {
                  name: prescriptionsByType.ASSESSMENT.fileName || "Assessment",
                  url: prescriptionsByType.ASSESSMENT.url
                } : null,
                exercises: prescriptionsByType.EXERCISE ? {
                  name: prescriptionsByType.EXERCISE.fileName || "Exercise", 
                  url: prescriptionsByType.EXERCISE.url
                } : null,
                clinicalConsultation: session.clinicalConsultation ? "Yes" : "No",
                medicalConsultation: session.medicalConsultation ? "Yes" : "No"
              }
            };
          })
          // Sort by date (most recent first)
          .sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB.getTime() - dateA.getTime();
          });

        setSessionsData(processedSessions);
      } else {
        // Even without appointment data, we can show member info
        setSessionsData([]);
      }
      
      setIsLoading(false);
      } else {
        console.log("PDFExport - No member data found in detail response");
      }
    } else if (!detailLoading && !appointmentLoading) {
      // If both API calls are complete but no data, show error state
      console.log("PDFExport - No detail data received, APIs completed");
      setIsLoading(false);
    }
    
    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.log("PDFExport - Timeout reached, stopping loading");
        setIsLoading(false);
        // If we don't have member data, create a fallback
        if (!memberData) {
          console.log("PDFExport - Setting fallback member data");
          setMemberData({
            firstName: "Sample",
            lastName: "Member",
            email: "sample@example.com",
            mobile: "1234567890",
            countryCode: "+91",
            gender: "male",
            location: "Sample Location, India",
            createdOn: "2024-01-15 10:30:00",
            dateOfBirth: "1990-05-20",
            emergencyContactName: "Emergency Contact",
            emergencyContactRelationship: "Family",
            emergencyContactNumber: "+91-9876543210",
            organisationName: "Sample Organization"
          });
        }
        
        // Also add some sample session data if none exists
        if (sessionsData.length === 0) {
          console.log("PDFExport - Setting fallback session data");
          setSessionsData([
            {
              appointmentId: "SAMPLE001",
              date: "2024-01-10",
              counsellorFirstName: "Dr. Sample",
              counsellorLastName: "Practitioner",
              status: "COMPLETED",
              mode: "Video",
              riskStatus: "LOW_RISK",
              prescriptions: [],
              sessionFindings: {
                selfHarmSymptoms: "None",
                depressionSymptoms: "Low",
                anxietySymptoms: "Moderate",
                angerIssues: "Low"
              },
              recommendations: {
                assessments: null,
                exercises: null,
                clinicalConsultation: "Yes",
                medicalConsultation: "No"
              }
            }
          ]);
        }
      }
    }, 5000);

    return () => clearTimeout(timeout);
  }, [appointmentData, detail, params, isLoading, memberData]);

  useEffect(() => {
    // Set document title for the PDF
    if (memberData) {
      const memberName = `${memberData.firstName || ''} ${memberData.lastName || ''}`.trim();
      document.title = `Member Summary Report - ${memberName}`;
    }
    
    return () => {
      // Reset title when leaving the page
      document.title = "PhyberCare Practitioner";
    };
  }, [memberData]);

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Helper function to get organisation logo from available data sources
  const getOrganisationLogo = () => {
    console.log("Getting organisation logo - Debug data:");
    console.log("detail:", detail);
    console.log("memberData:", memberData);
    console.log("organisationData:", organisationData);
    
    // Priority 1: From appointment detail API (member's organisation logo) 
    if (detail && detail.organisationLogo && detail.organisationLogo !== null) {
      console.log("Using logo from detail:", detail.organisationLogo);
      return detail.organisationLogo;
    }
    
    // Priority 2: From member data extracted from detail
    if (memberData && memberData.organisationLogo && memberData.organisationLogo !== null) {
      console.log("Using logo from memberData:", memberData.organisationLogo);
      return memberData.organisationLogo;
    }
    
    // Priority 3: From counsellor/organisation API - correct Redux store structure
    if (organisationData && organisationData.status === 'success' && organisationData.message && organisationData.message.payload && organisationData.message.payload.organisationLogo && organisationData.message.payload.organisationLogo !== null) {
      console.log("Using logo from organisationData:", organisationData.message.payload.organisationLogo);
      return organisationData.message.payload.organisationLogo;
    }
    
    console.log("No organisation logo found, using fallback");
    return null;
  };

  // Show loading if data is not ready
  if (isLoading || !memberData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-semibold text-[#4B61FF] mb-2">Preparing Report...</div>
          <div className="text-sm text-[#334155]">Please wait while we generate your PDF report</div>
        </div>
      </div>
    );
  }

  // Calculate overview stats from sessions data
  const overviewStats = {
    recentRiskStatus: sessionsData.length > 0 ? sessionsData[0].riskStatus || "NO_RISK" : "NO_RISK",
    totalAppointments: sessionsData.length,
    completed: sessionsData.filter(s => s.status === "COMPLETED").length,
    followUps: 0, // This would need additional API data
    prescriptionsIssued: sessionsData.filter(s => s.prescriptions && s.prescriptions.length > 0).length
  };

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* PDF Download Button */}
        <PDFDownloadButton memberData={memberData} />

        {/* Fixed Header and Footer for PDF pages (hidden on screen) */}
        <div className="pdf-header-running hidden">
          <PDFHeader />
        </div>
        <div className="pdf-footer-running hidden">
          <PDFFooter />
        </div>

        {/* PDF Content Container */}
        <div className="pdf-content max-w-[1728px] mx-auto p-8 font-poppins text-[#171717]">
        
        {/* Header - visible on screen, hidden in print */}
        <div className="print:hidden">
          <PDFHeader />
        </div>
        
        {/* Logo Section */}
        <LogoSection organisationLogo={getOrganisationLogo()} />

        {/* Document Header */}
        <div className="flex flex-row justify-between items-center p-0 gap-[582px]">
          <div className="font-poppins font-bold relative shrink-0 text-[48px]">
            <p className="leading-[normal] text-nowrap whitespace-pre">Member Summary Report</p>
          </div>
          <div className="font-poppins font-semibold relative shrink-0 text-[0px]">
            <p className="leading-[normal] not-italic text-[20px] text-nowrap whitespace-pre">
              <span className="font-poppins font-semibold">Report Date: </span>
              <span className="font-poppins font-normal">{formatDate()}</span>
            </p>
          </div>
        </div>

        {/* Basic Information Section */}
        <div className="bg-white box-border content-stretch flex items-start justify-between p-[16px] relative rounded-[16px] mb-12">
          <div aria-hidden="true" className="absolute border border-[#E2E8F0] gap-y-4 border-solid inset-0 pointer-events-none rounded-[16px]" />
          
          {/* Basic Information */}
          <div className="content-stretch flex flex-col gap-y-[16px] items-start justify-start relative shrink-0">
            <div className="flex flex-col font-poppins font-semibold justify-center leading-[0] not-italic font-semibold relative shrink-0 text-[#4B61FF] text-[16px] text-nowrap tracking-[0.32px]">
              <p className="leading-[24px] whitespace-pre">Basic Information</p>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Member Name:</p>
              </div>
              <div className="font-poppins font-medium relative shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{`${memberData.firstName || ''} ${memberData.lastName || ''}`.trim()}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Contact Number:</p>
              </div>
              <div className="font-poppins font-medium relative shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{`${memberData.countryCode || ''}${memberData.mobile || ''}`}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Email Address:</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.email || 'N/A'}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Joined On:</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{formatApiDate(memberData.createdOn ? memberData.createdOn.split(' ')[0] : '') || 'N/A'}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Gender:</p>
              </div>
              <div className="font-poppins font-medium relative shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.gender ? memberData.gender.charAt(0).toUpperCase() + memberData.gender.slice(1) : 'N/A'}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Age / DOB:</p>
              </div>
              <div className="font-poppins font-medium relative shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{calculateAge(memberData.dateOfBirth) || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Vertical Separator */}
          <div className="w-px bg-[#CBD5E1] self-stretch mx-4" style={{height: 'auto'}}></div>

          {/* Organizational Information */}
          <div className="content-stretch flex flex-col gap-y-[16px] items-start justify-start relative shrink-0">
            <div className="flex flex-col font-poppins font-semibold justify-center leading-[0] font-semibold not-italic relative shrink-0 text-[#4B61FF] text-[16px] text-nowrap tracking-[0.32px]">
              <p className="leading-[24px] whitespace-pre">Organizational Information</p>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Name of the University:</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717] text-right">
                <p className="leading-[normal] text-nowrap whitespace-pre">N/A</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Department:</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">N/A</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Location:</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.location || 'N/A'}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Custom Groups:</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717] text-right">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.organisationName || 'N/A'}</p>
              </div>
            </div>
          </div>

            
          
          {/* Vertical Separator */}
          <div className="w-px bg-[#CBD5E1] self-stretch mx-4" style={{height: 'auto'}}></div>

          {/* Emergency Contact Details */}
          <div className="content-stretch flex flex-col gap-y-[16px] items-start justify-start relative shrink-0">
            <div className="flex flex-col font-poppins font-semibold justify-center leading-[0] font-semibold not-italic relative shrink-0 text-[#4B61FF] text-[16px] text-nowrap tracking-[0.32px]">
              <p className="leading-[24px] whitespace-pre">Emergency Contact Details</p>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Name</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.emergencyContactName || 'N/A'}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Relationship</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717] text-right">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.emergencyContactRelationship || 'N/A'}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Contact Number</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717] text-right">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.emergencyContactNumber || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Overview Stats Section */}
        <div className="overview-stats mb-12">
          <h2 className="font-poppins font-semibold text-[#171717] text-[24px] uppercase tracking-[0.48px] mb-3">
            Overview Stats
          </h2>
          <div className="bg-white border border-[#E2E8F0] rounded-[16px] p-4">
            <div className="flex gap-6 justify-between">
              <div className="flex flex-col gap-4 flex-1">
                <div className="flex justify-between items-center w-[460px]">
                  <span className="font-poppins font-normal text-[#334155] text-[16px] ">Recent Risk Status</span>
                  <RiskStatusBadge risk={overviewStats.recentRiskStatus} />
                </div>
                <div className="flex justify-between w-[460px]">
                  <span className="font-poppins font-normal text-[#334155] text-[16px]">Total Appointments</span>
                  <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">{overviewStats.totalAppointments}</span>
                </div>
              </div>
              
              <div className="w-px bg-[#CBD5E1] self-stretch" style={{height: '65px'}}></div>
              
              <div className="flex flex-col gap-4 flex-1">
                <div className="flex justify-between w-[460px]">
                  <span className="font-poppins font-normal text-[#334155] text-[16px]">Completed</span>
                  <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">{overviewStats.completed}</span>
                </div>
                <div className="flex justify-between w-[460px]">
                  <span className="font-poppins font-normal text-[#334155] text-[16px]">Follow-ups</span>
                  <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">{overviewStats.followUps}</span>
                </div>
              </div>
              
              <div className="w-px bg-[#CBD5E1] self-stretch" style={{height: '65px'}}></div>
              
              <div className="flex justify-between  flex-1 w-[460px]">
                <span className="font-poppins font-normal text-[#334155] text-[16px]">Prescriptions Issued</span>
                <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">{overviewStats.prescriptionsIssued}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="flex justify-center mb-12">
          <div className="h-px bg-[#E2E8F0] w-[350px]"></div>
        </div>

        {/* Session Details Section */}
        <div className="sessions-section mb-12">
          {/* Session Header */}
          <div className="bg-[#EFF6FF] border border-[#E2E8F0] rounded-[8px] px-3 py-1 mb-6 flex justify-between items-center">
            <h2 className="font-poppins font-semibold text-[#171717] font-semibold text-[24px] uppercase">
              Session Details
            </h2>
            <div className="font-poppins font-semibold text-[20px] font-semibold">
              <span>Total Sessions: </span>
              <span className="font-medium">{sessionsData.length}</span>
            </div>
          </div>

          {/* Sessions */}
          <div className="flex flex-col gap-3">
            {sessionsData.map((session, index) => {
              // Calculate chronological session number (reverse of array index + 1)
              const sessionNumber = sessionsData.length - index;
              
              return (
                <div key={session.appointmentId || index} className="session-card">
                  {/* Session Summary Card */}
                  <div className="bg-white border border-[#E2E8F0] rounded-[16px] p-4 mb-3">
                    <div className="flex gap-8">
                      {/* Session Info */}
                      <div className="flex flex-col gap-4 flex-1">
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Consultation</span>
                          <span className="font-poppins font-semibold text-[#4B61FF] text-[18px] font-semibold">{sessionNumber}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Appointment ID</span>
                          <span className="font-poppins font-semibold text-[#4B61FF] text-[18px] font-semibold">{session.appointmentId}</span>
                        </div>
                      </div>
                      
                      <div className="w-px bg-[#CBD5E1] self-stretch" style={{height: '97px'}}></div>
                      
                      {/* Date and Practitioner */}
                      <div className="flex flex-col gap-4 flex-1">
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Date</span>
                          <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">{formatApiDate(session.date)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Practitioner Name</span>
                          <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">{`${session.counsellorFirstName || ''} ${session.counsellorLastName || ''}`.trim() || 'N/A'}</span>
                        </div>
                      </div>
                      
                      <div className="w-px bg-[#CBD5E1] self-stretch" style={{height: '97px'}}></div>
                      
                      {/* Status and Risk */}
                      <div className="flex flex-col gap-3 flex-1">
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Status</span>
                          <StatusIndicator session={session} />
                        </div>
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Mode</span>
                          <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">{session.mode || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Overall Risk</span>
                          <RiskStatusBadge risk={session.riskStatus || "NO_RISK"} />
                        </div>
                      </div>
                      
                      <div className="w-px bg-[#CBD5E1] self-stretch" style={{height: '97px'}}></div>
                      
                      {/* Prescriptions and Notes */}
                      <div className="flex flex-col gap-3 flex-1">
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Prescriptions</span>
                          <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">
                            {session.prescriptions && session.prescriptions.length > 0 ? "Added" : "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Notes</span>
                          <span className="font-poppins font-medium text-[#171717] font-medium text-[16px]">
                            {session.notesText || session.recommendationText ? "Added" : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Session Findings Card */}
                  <div className="bg-white border border-[#E2E8F0] rounded-[16px] p-4 mb-3">
                    <div className="flex flex-col gap-2">
                      <h4 className="font-poppins font-semibold text-[#4B61FF] text-[18px] underline">
                        Session Findings
                      </h4>
                      <div className="flex flex-col gap-2">
                        {Object.entries(session.sessionFindings || {}).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="font-poppins font-normal text-[#334155] text-[16px]">
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </span>
                            <span className="font-poppins font-medium text-[#171717] font-medium text-[16px]">{value || 'N/A'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Recommendations Card */}
                  <div className="bg-white border border-[#E2E8F0] rounded-[16px] p-4">
                    <div className="flex flex-col gap-2">
                      <h4 className="font-poppins font-semibold text-[#4B61FF] text-[18px] underline">
                        Recommendations
                      </h4>
                      <div className="flex flex-col gap-2">
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px] font-medium">
                            Assessments
                          </span>
                          <span className="font-poppins font-normal text-[#171717] text-[16px]">
                            {session.recommendations?.assessments?.name || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px] font-medium">
                            Exercises
                          </span>
                          <span className="font-poppins font-normal text-[#171717] text-[16px]">
                            {session.recommendations?.exercises?.name || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px] font-medium">
                            Clinical Consultation
                          </span>
                          <span className="font-poppins font-normal text-[#171717] text-[16px]">
                            {session.recommendations?.clinicalConsultation || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px] font-medium">
                            Medical Consultation
                          </span>
                          <span className="font-poppins font-normal text-[#171717] text-[16px]">
                            {session.recommendations?.medicalConsultation || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Separator between sessions */}
                  {index < sessionsData.length - 1 && (
                    <div className="flex justify-center my-6">
                      <div className="h-0.5 bg-[#E2E8F0] w-full"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer - visible on screen, hidden in print */}
        <div className="print:hidden">
          <PDFFooter />
        </div>
      </div>
      </div>

      {/* Print-specific CSS for proper PDF generation */}
      <style jsx>{`
        @media print {
          @page {
            size: A3;
            margin-top: 0.75in;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
            font-family: 'Poppins', sans-serif;
          }
          
          .pdf-content {
            max-width: none !important;
            margin: 0 !important;
            padding: 80px 0 100px 0 !important;
          }
          
          /* Fixed header and footer for every page */
          .pdf-header-running {
            position: fixed;
            
            
            background: white;
            display: block !important;
          }
          
          .pdf-footer-running {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            height: 100px;
            z-index: 1000;
            background: white;
            display: block !important;
          }
          
          /* Hide non-essential elements during print */
          .print\\:hidden {
            display: none !important;
          }
          
          /* Ensure proper page breaks */
          .session-card {
            break-inside: avoid;
            page-break-inside: avoid;
            margin-bottom: 20px;
          }
          
          /* Ensure sections don't break */
          .overview-stats,
          .basic-info-section {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          
          /* Better font rendering for print */
          * {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          
          /* Ensure colors are preserved */
          .bg-\\[\\#4B61FF\\] {
            background-color: #4B61FF !important;
          }
          
          .bg-\\[\\#DC2626\\] {
            background-color: #DC2626 !important;
          }
          
          .bg-\\[\\#FF7439\\] {
            background-color: #FF7439 !important;
          }
          
          .bg-\\[\\#FFEE33\\] {
            background-color: #FFEE33 !important;
          }
          
          .bg-\\[\\#B7E049\\] {
            background-color: #B7E049 !important;
          }
          
          .bg-\\[\\#98FF8A\\] {
            background-color: #98FF8A !important;
          }
        }
      `}</style>
    </>
  );
}